import React, { useState, useEffect, useRef } from "react";
import { FaPaperPlane } from "react-icons/fa";
import CryptoJS from "crypto-js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getChatApiBase } from "./utils/widgetConfig";
import LeadCaptureBottomSheet from "./LeadCaptureBottomSheet";
import ScheduleMeeting from "./MeetingForm";
import "./ChatWidget.css";

const API_BASE_URL = getChatApiBase();
const conversationId = `conv_${Date.now()}_${Math.floor(
  Math.random() * 100000
)}`;
const WealthLogo =
  "https://jamaappprod.s3.ap-south-1.amazonaws.com/static/jewelbuild/OmnimaxIcon.png";

// ---- ENCRYPTION UTILS ----
const SECRET_KEY_HEX = "fa24bd2573919aff52897715b3e60c74";

const ProspectChatBot = ({ initialMessage, onInitialMessageSent }) => {
  const pendingFollowupsRef = useRef(null);
  const customFollowupsRef = useRef(null);
  const customForMessageRef = useRef(null);
  const [idleCount, setIdleCount] = useState(0);
  const hasTriggeredRef = useRef(false);
  const [rankedChunkIds, setRankedChunkIds] = useState([]);

  // Initialize token from localStorage
  const [token, setToken] = useState(() => {
    return localStorage.getItem('maxiom_auth_token') || null;
  });

  const idleTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamingMessageIdRef = useRef(null);

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Hello, I am OM from Maxiom. Here to help you on investments, wealth creation, financial planning.",
      timestamp: new Date().toLocaleTimeString(),
      followup_options: [
        { id: "1", text: "I want to invest in Mutual Funds" },
        { id: "2", text: "I want to grow my wealth" },
        { id: "3", text: "I want to save tax" },
        { id: "4", text: "I want retirement planning" },
        { id: "5", text: "I have a financial goal" },
        { id: "6", text: "I want FREE review of my portfolio" },
        { id: "7", text: "I want insurance planning" },
        { id: "8", text: "Talk to an advisor" },
      ],
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [idleTimeout, setIdleTimeout] = useState(6000000);

  const [uid, setUid] = useState("");
  const [zohoVisitorData, setZohoVisitorData] = useState({});
  const [leadActivities, setLeadActivities] = useState([]);
  const [isLoadingVisitor, setIsLoadingVisitor] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [leadCaptureMode, setLeadCaptureMode] = useState(false);
  const [leadCaptureStep, setLeadCaptureStep] = useState(null);
  const [customFollowups, setCustomFollowups] = useState(null);
  const [leadInfo, setLeadInfo] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    capturedAt: null,
    user_uniqueid: "",
  });
  const [isLeadCaptured, setIsLeadCaptured] = useState(false);
  const [leadFormTitle, setLeadFormTitle] = useState("For more information");
  const [leadFormReason, setLeadFormReason] = useState(null);
  const [allowLeadFormSkip, setAllowLeadFormSkip] = useState(true);
  const [isExistingUser, setIsExistingUser] = useState(false);

  const fetchVisitorData = async (uniqueId) => {
    if (!uniqueId) return;
    setIsLoadingVisitor(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/zoho/visitor?user_uniqueid=${encodeURIComponent(
          uniqueId
        )}`
      );
      const data = await response.json();

      if (data.success && data.visitor) {
        setZohoVisitorData({ ...data.visitor });
        const Leaddata = await createLead(data.visitor);
        if (data.visitor.email) {
          setIsLeadCaptured(true);
        }
        // Capture lead activities from Leaddata
        if (Leaddata?.activities && Array.isArray(Leaddata.activities)) {
          setLeadActivities(Leaddata.activities);
          console.log(
            "Lead Activities captured:",
            Leaddata.activities.length,
            "items"
          );
        }
        console.log("Zoho Visitor Data:", data.visitor);
      } else {
        console.log("Visitor not found");

        const data = await createLead();
        if (data.lead.email) {
          setIsLeadCaptured(true);
        }
        // Capture lead activities from data
        if (data?.activities && Array.isArray(data.activities)) {
          setLeadActivities(data.activities);
          console.log(
            "Lead Activities captured:",
            data.activities.length,
            "items"
          );
        }
        setZohoVisitorData(data.lead);
        await createUser(data.lead);
      }
    } catch (err) {
      console.error("Error fetching visitor data:", err);
      setZohoVisitorData({});
    } finally {
      setIsLoadingVisitor(false);
    }
  };

  useEffect(() => {
    if (uid) {
      fetchVisitorData(uid);
    }
  }, [uid]);

  // Save token to localStorage whenever it changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('maxiom_auth_token', token);
      console.log('Token saved to localStorage');

      // Resolve pending lead form promise when token is set - pass the token value
      if (window._resolveLeadForm) {
        console.log('Token set, resolving lead form with token:', token);
        window._resolveLeadForm(token);
        window._resolveLeadForm = null;
      }
    } else {
      localStorage.removeItem('maxiom_auth_token');
      console.log('Token removed from localStorage');
    }
  }, [token]);

  // Auto-fetch token when zohoVisitorData updates
  useEffect(() => {
    const fetchTokenFromVisitorData = async () => {
      if (zohoVisitorData?.phone && !token) {
        console.log("Visitor data updated, checking if user exists...");
        const userCheck = await UserExistchecks(zohoVisitorData.phone);
        if (userCheck.exists && userCheck.rToken) {
          console.log("Token fetched from visitor data");
        }
      }
    };

    fetchTokenFromVisitorData();
  }, [zohoVisitorData]);

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(handleUserIdle, idleTimeout);
  };

  useEffect(() => {
    resetIdleTimer();
  }, []);

  const handleUserIdle = () => {
    if (isLoading) return;

    setIdleCount((prev) => {
      if (prev >= 2) {
        console.log("Idle limit reached, not sending again.");
        return prev;
      }

      const newCount = prev + 1;
      console.log(`Idle fired (${newCount}/2)`);

      if (newCount === 2) {
        if (!isLeadCaptured || !token) {
          hasTriggeredRef.current = true;
          setShowLeadForm(true);
        }
      } else {
        handleSendMessage("__IDLE_TRIGGER___");
      }
      setIdleTimeout((t) => t * 2);
      return newCount;
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        window.$zoho &&
        window.$zoho.salesiq &&
        window.$zoho.salesiq.visitor
      ) {
        const visitorId = window.$zoho.salesiq.visitor.uniqueuserid();
        if (visitorId) {
          setUid(visitorId);
          console.log("Zoho Visitor ID:", visitorId);
          clearInterval(interval);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  async function createLead(visitor_data) {
    let payload = {};
    if (visitor_data) {
      payload = {
        visitor_id: uid,
        page_url: window.location.href,
        visitor_data: visitor_data,
        conversation_id: conversationId,
        source: "CHAT_BOT",
      };
    } else {
      payload = {
        visitor_id: uid,
        page_url: window.location.href,
        source: "CHAT_BOT",
      };
    }

    try {
      const response = await fetch(
        "https://regular.docker.jama.co.in/app/api/v2/zoho/get_create_lead/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      console.log("Lead Response:", data);
      return data;
    } catch (error) {
      console.error("createLead error:", error);
      return null;
    }
  }

  async function updateLead(visitor_data) {
    const payload = {
      visitor_id: uid,
      visitor_data: visitor_data || {},
      email: visitor_data.email,
      phone: visitor_data.phone,
    };
    console.log(payload);
    try {
      const response = await fetch(
        "https://regular.docker.jama.co.in/app/api/v2/zoho/update_lead_activity/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      console.log(":", data);
      return data;
    } catch (error) {
      console.error("createLead error:", error);
      return null;
    }
  }

  function encryptPayload(payload) {
    const secret_key = CryptoJS.enc.Hex.parse(SECRET_KEY_HEX);
    const iv = CryptoJS.lib.WordArray.random(16);

    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), secret_key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const encryptedData = iv.concat(encrypted.ciphertext).toString(
      CryptoJS.enc.Base64
    );

    return encryptedData;
  }

  const UserExistchecks = async (phone) => {
    try {
      const payload = {
        username: phone,
      };

      const encryptedBody = encryptPayload(payload);

      const response = await fetch(
        "https://regular.maxiom.in/app/api/get_database_of_user/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ payload: encryptedBody }),
        }
      );

      const data = await response.json();
      console.log("User exists check:", data);

      const userExists = data.results?.user_exists_in_club === "TRUE";
      if (userExists && data.results?.rToken) {
        setToken(data.results.rToken);
        console.log("User exists in club, rToken loaded");
      }

      return {
        exists: userExists,
        data: data.results,
        rToken: data.results?.rToken,
      };
    } catch (error) {
      console.error("Check User Exists API Error:", error);
      return { exists: false, error: error.message };
    }
  };

  const createUser = async (userData) => {
    try {
      let payload = {
        username: userData.phone,
        first_name: userData.name,
        email: userData.email,
        source: "",
        platform: "CHATBOT",
        referred_by: "",
        corp_code: "",
        otp: userData.phone_otp,
        is_otp_verfied: true,
        campaign: "",
        database_host: "club.maxiomwealth.com",
        subscription_plan_code: "",
        corporate_x: "",
      };
      const response = await fetch(
        "https://regular.maxiom.in/app/api/rest-auth/register/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create user");
      }

      const data = await response.json();
      const userToken = data.token.split("").reverse().join("");
      setToken(userToken);

      // Call CRM deals API after successful user creation
      try {
        await fetch(
          "https://regular.maxiom.in/app/api/create_crmdeals_cip_for_regular_clients/",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `token ${userToken}`,
            },
          }
        );
        console.log("CRM deals API called successfully");
      } catch (crmError) {
        console.error("CRM deals API Error:", crmError);
      }

      return { success: true, data };
    } catch (error) {
      console.error("Create User API Error:", error);
      return { success: false, error: error.message };
    }
  };

  async function sendWebhookTest(visitor_data) {
    const transcript = buildTranscript();
    const payload = {
      visitor_id: uid,
      transcript: transcript,
      activity_type: "chat_interaction",
      conversation_id: conversationId,
      async: false,
    };

    const res = await fetch(
      "https://regular.docker.jama.co.in/app/api/v2/zoho/update_lead_activity/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await res.json();
    console.log("Webhook Response:", data);
  }

  useEffect(() => {
    if (messages.length === 0 || !uid) return;

    const lastMessage = messages[messages.length - 1];

    if (lastMessage.isStreaming || lastMessage.streaming) return;
    sendWebhookTest(lastMessage);

    if (
      !hasTriggeredRef.current &&
      messages.length >= 6 &&
      (!isLeadCaptured || !token) &&
      !showLeadForm &&
      lastMessage.type === "bot"
    ) {
      setTimeout(() => setShowLeadForm(true), 5000);
      hasTriggeredRef.current = true;
    }
  }, [messages, uid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  function buildTranscript() {
    return messages
      .filter((m) => !m.isStreaming)
      .map((m) => ({
        timestamp: new Date().toISOString(),
        sender: m.type === "user" ? "user" : "bot",
        message: m.text,
      }));
  }

  const detectHappiness = (text) => {
    const happyKeywords = [
      "thank", "thanks", "great", "awesome", "perfect", "excellent",
      "wonderful", "amazing", "helpful", "appreciate", "love it",
      "good", "nice", "satisfied", "happy", "exactly", "right",
      "yes", "okay", "ok", "sounds good", "that works", "interested",
    ];

    const lowerText = text.toLowerCase();
    return happyKeywords.some((keyword) => lowerText.includes(keyword));
  };

  const collectedDataRef = useRef({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const processLeadCaptureInput = (inputText) => {
    const trimmedInput = inputText.trim();

    collectedDataRef.current[leadCaptureStep] = trimmedInput;
    setLeadInfo((prev) => ({
      ...prev,
      [leadCaptureStep]: trimmedInput,
    }));

    let botResponse = "";
    let nextStep = null;

    switch (leadCaptureStep) {
      case "name":
        botResponse = `Nice to meet you, ${trimmedInput}! What's your email address?`;
        nextStep = "email";
        break;
      case "email":
        botResponse = "What's the best phone number to reach you?";
        nextStep = "phone";
        break;
      case "phone":
        botResponse = 'What company do you work for? (Optional - type "skip" to skip)';
        nextStep = "company";
        break;
      case "company":
        if (trimmedInput.toLowerCase() === "skip") {
          collectedDataRef.current.company = "";
        }
        botResponse = 'Is there anything specific you\'d like us to know or help you with? (Optional - type "skip" to skip)';
        nextStep = "message";
        break;
      case "message":
        if (trimmedInput.toLowerCase() === "skip") {
          collectedDataRef.current.message = "";
        }

        const finalLead = {
          name: collectedDataRef.current.name,
          email: collectedDataRef.current.email,
          phone: collectedDataRef.current.phone,
          company: collectedDataRef.current.company,
          message: collectedDataRef.current.message,
          capturedAt: new Date().toISOString(),
        };

        console.log("Sending to Zoho SalesIQ:", finalLead);

        try {
          if (window.$zoho && window.$zoho.salesiq) {
            window.$zoho.salesiq.visitor.name(finalLead.name);
            window.$zoho.salesiq.visitor.email(finalLead.email);
            window.$zoho.salesiq.visitor.contactnumber(finalLead.phone);
            window.$zoho.salesiq.visitor.info({
              Company: finalLead.company || "",
              Message: finalLead.message || "",
              CapturedAt: finalLead.capturedAt,
            });
            console.log("Lead data sent to Zoho SalesIQ successfully");
          } else {
            console.warn("Zoho SalesIQ not available");
          }
        } catch (err) {
          console.error("Zoho SalesIQ Lead Sync Error:", err);
        }

        setLeadInfo({
          ...finalLead,
          user_uniqueid: uid,
        });
        setIsLeadCaptured(true);
        setLeadCaptureMode(false);
        setLeadCaptureStep(null);

        collectedDataRef.current = {
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
        };

        botResponse = "Thank you! Your information has been saved. Our team will reach out to you soon. Feel free to ask me any other questions!";
        break;
    }

    const botMessage = {
      id: Date.now() + 1,
      type: "bot",
      text: botResponse,
      timestamp: new Date().toLocaleTimeString(),
      isLeadCapture: leadCaptureStep !== "message",
    };

    setMessages((prev) => [...prev, botMessage]);

    if (nextStep) {
      setLeadCaptureStep(nextStep);
    }
  };

  const cleanBotText = (txt) => {
    if (!txt) return "";
    return txt
      .replace(/"followup_options```?/gi, "")
      .replace(/followup_options/gi, "")
      .trim();
  };

  function waitForLeadFormSubmit() {
    return new Promise((resolve) => {
      window._resolveLeadForm = resolve;
    });
  }

  const handleSendMessage = async (questionText = null) => {
    resetIdleTimer();
    const questionToSend = questionText || input;

    if (!questionToSend?.trim() || isLoading) return;
    if (questionToSend !== "__IDLE_TRIGGER___") {
      const userMessage = {
        id: Date.now(),
        type: "user",
        text: questionToSend,
        timestamp: new Date().toLocaleTimeString(),
      };

      setMessages((prev) => [...prev, userMessage]);
      if (questionToSend === "🚀 Start Mutual Fund Investment") {
        if (!token) {
          setLeadFormTitle("To start your mutual fund investment, please provide email and phone number");
          setAllowLeadFormSkip(false);
          setShowLeadForm(true);
          const newToken = await waitForLeadFormSubmit();
          setAllowLeadFormSkip(true);
          const redirectUrl = isExistingUser
            ? `/explore/mutualfunds`
            : `/onboarding/signupimportportfolio`;
          window.open(
            `https://dclub.maxiomwealth.com/login?unique=${newToken}&redirection_url=${redirectUrl}`,
            "_blank"
          );
        } else {
          window.open(
            `https://dclub.maxiomwealth.com/login?unique=${token}&redirection_url=/explore/mutualfunds`,
            "_blank"
          );
        }
        setIsLoading(false);
        setInput("");
        return;
      }
      if (questionToSend === "Schedule a Meeting") {
        setShowMeetingForm(true);
        setIsLoading(false);
        setInput("");
        return;
      }
      if (questionToSend === "Start Financial Planning") {
        if (!token) {
          setLeadFormTitle("To start your financial planning, please provide email and phone number");
          setAllowLeadFormSkip(false);
          setShowLeadForm(true);
          const newToken = await waitForLeadFormSubmit();
          setAllowLeadFormSkip(true);
          const redirectUrl = isExistingUser
            ? `/goals/intro`
            : `/onboarding/signupfinancialplan`;
          window.open(
            `https://club.maxiomwealth.com/login?unique=${newToken}&next=FINPLAN&redirection_url=${redirectUrl}`,
            "_blank"
          );
        } else {
          window.open(
            `https://club.maxiomwealth.com/login?unique=${token}&next=FINPLAN&redirection_url=/goals/intro`,
            "_blank"
          );
        }
        setIsLoading(false);
        setInput("");
        return;
      }
      if (questionToSend === "Get Portfolio Analysis") {
        if (!token) {
          setLeadFormTitle("To get your portfolio analysis, please provide email and phone number");
          setAllowLeadFormSkip(false);
          setShowLeadForm(true);
          const newToken = await waitForLeadFormSubmit();
          setAllowLeadFormSkip(true);
          const redirectUrl = isExistingUser
            ? `/dashboard/importstocks?from=dashboard`
            : `/onboarding/signupimportportfolio`;
          window.open(
            `https://club.maxiomwealth.com/login?unique=${newToken}&redirection_url=${redirectUrl}`,
            "_blank"
          );
        } else {
          window.open(
            `https://club.maxiomwealth.com/login?unique=${token}&redirection_url=/dashboard/importstocks?from=dashboard`,
            "_blank"
          );
        }
        setIsLoading(false);
        setInput("");
        return;
      }
    }

    setInput("");

    if (leadCaptureMode) {
      processLeadCaptureInput(questionToSend);
      return;
    }

    const isHappy = detectHappiness(questionToSend);
    setIsLoading(true);

    try {
      const url = `${API_BASE_URL}/api/prospect/query`;

      console.log("Starting POST streaming:", url);

      const body = {
        question: questionToSend,
        session_id: sessionId || null,
        conversation_history: messages.map((m) => ({
          role: m.type === "user" ? "user" : "assistant",
          content: m.text,
        })),
        research_mode: "fast_research",
        user_id: "anonymous",
        stream: true,
        zoho_visitor: zohoVisitorData || {},
        lead_activity: leadActivities || [],
        rankedChunkIds: rankedChunkIds || [],
      };

      console.log("Sending lead_activity:", leadActivities.length, "activities");
      console.log("Sending zoho_visitor:", Object.keys(zohoVisitorData).length, "fields");

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.body) {
        throw new Error("No stream returned");
      }

      let streamingId = Date.now() + 1;
      streamingMessageIdRef.current = streamingId;

      let reader = response.body.getReader();
      let decoder = new TextDecoder();
      let accumulatedText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (let line of lines) {
          if (!line.trim()) continue;

          let jsonStr = line;
          if (line.startsWith("data: ")) {
            jsonStr = line.substring(6);
          }

          if (!jsonStr.trim()) continue;

          let data;
          try {
            data = JSON.parse(jsonStr);
          } catch (parseError) {
            console.warn("Failed to parse SSE event:", jsonStr);
            continue;
          }

          switch (data.type) {
            case "session_id":
              setSessionId(data.session_id);
              break;

            case "answer_chunk": {
              let chunkText = data.chunk;
              resetIdleTimer();
              accumulatedText += chunkText;

              setMessages((prev) => {
                const updated = [...prev];
                const index = updated.findIndex((m) => m.id === streamingId);

                if (index !== -1) {
                  updated[index].text = accumulatedText;
                } else {
                  updated.push({
                    id: streamingId,
                    type: "bot",
                    text: accumulatedText,
                    isStreaming: true,
                    timestamp: new Date().toLocaleTimeString(),
                  });
                }
                return updated;
              });
              break;
            }

            case "show_lead_form":
              if ((!isLeadCaptured || !token) && data.show_lead_form) {
                setTimeout(() => setShowLeadForm(true), 5000);
              }
              break;

            case "ranked_chunk_ids":
              setRankedChunkIds(data.data || []);
              console.log(data.data);
              break;

            case "is_invest_in_mutual_fund":
              if (data.is_invest_in_mutual_fund) {
                customForMessageRef.current = streamingId;
                customFollowupsRef.current = [
                  { id: "1", text: "Schedule a Meeting" },
                  { id: "2", text: "Start Mutual Fund Investment" },
                ];
              }
              break;

            case "is_create_financial_plan":
              if (data.is_create_financial_plan) {
                customForMessageRef.current = streamingId;
                customFollowupsRef.current = [
                  { id: "1", text: "Schedule a Meeting" },
                  { id: "2", text: "Start Financial Planning" },
                ];
              }
              break;

            case "is_portfolio_analysis":
              if (data.is_portfolio_analysis) {
                customForMessageRef.current = streamingId;
                customFollowupsRef.current = [
                  { id: "1", text: "Schedule a Meeting" },
                  { id: "2", text: "Get Portfolio Analysis" },
                ];
              }
              break;

            case "is_ask_for_book_meeting":
              if (data.is_ask_for_book_meeting) {
                setTimeout(() => setShowMeetingForm(true), 2000);
              }
              break;

            case "end": {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamingId ? { ...m, isStreaming: false } : m
                )
              );
              break;
            }

            case "followup_options":
              console.log("Received followup options:", data.options);
              var options = [
                { id: "1", text: "Schedule a Meeting" },
                { id: "2", text: "Start Financial Planning" },
              ];
              const mustUseCustom = customForMessageRef.current === streamingId;
              if (mustUseCustom) {
                // options = [...(customFollowupsRef.current || []), ...(data.options || [])];
                options = customFollowupsRef.current;
                console.log(options);
                // Clear refs immediately to ensure this only runs once
                customFollowupsRef.current = null;
                customForMessageRef.current = null;
              }

              setMessages((prev) => {
                const updated = [...prev];
                const index = updated.findIndex((m) => m.id === streamingId);

                if (index !== -1) {
                  updated[index] = {
                    ...updated[index],
                    followup_options: mustUseCustom ? options : data.options,
                  };
                }

                return updated;
              });
              break;

            case "final_response":
              setMessages((prev) => {
                const updated = [...prev];
                const index = updated.findIndex((m) => m.id === streamingId);

                if (index !== -1) {
                  updated[index] = {
                    ...updated[index],
                    text: cleanBotText(data.answer || accumulatedText),
                    isStreaming: false,
                    followup_options: updated[index].followup_options || [],
                    metadata: {
                      processingTime: data.total_time,
                      sources: data.sources,
                    },
                  };
                }

                return updated;
              });
              break;
          }
        }
      }

      setIsLoading(false);
      streamingMessageIdRef.current = null;

    } catch (err) {
      console.error("Streaming POST error:", err);
      setIsLoading(false);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "bot",
          text: "Sorry, something went wrong.",
          timestamp: new Date().toLocaleTimeString(),
          isError: true,
        },
      ]);
    }
  };

  const handleLeadFormSubmit = async (form) => {
    if (!form || Object.keys(form).length === 0) {
      console.warn("Form is empty or skipped — no Zoho update.");

      setLeadCaptureMode(false);
      setIsLeadCaptured(false);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "bot",
          text: "Okay! Skipped the form. What would you like to do now?",
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);

      return;
    }
    setLeadCaptureMode(false);

    const finalLead = {
      ...form,
      capturedAt: new Date().toISOString(),
      user_uniqueid: uid,
    };

    // Check if user already exists
    console.log("Checking if user exists...");
    const userCheck = await UserExistchecks(finalLead.phone);

    let apiResponse;
    if (userCheck.exists) {
      console.log("User already exists, skipping registration");
      setIsExistingUser(true);
      apiResponse = { success: true, exists: true, data: userCheck.data };
    } else {
      console.log("User doesn't exist, creating new user");
      setIsExistingUser(false);
      apiResponse = await createUser(finalLead);
    }

    try {
      if (window.$zoho && window.$zoho.salesiq) {
        window.$zoho.salesiq.visitor.name(finalLead.name);
        window.$zoho.salesiq.visitor.email(finalLead.email);
        window.$zoho.salesiq.visitor.contactnumber(finalLead.phone);
      }
    } catch (e) {
      console.error("Zoho Update Error:", e);
    }

    setLeadInfo(finalLead);
    setIsLeadCaptured(true);
    updateLead({ ...finalLead });

    setZohoVisitorData({ ...finalLead });
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: "bot",
        text: "Thanks! Your information has been saved and is 100% secure.",
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
  };

  // Handle initial message from compact bar (must be after handleSendMessage is defined)
  useEffect(() => {
    if (initialMessage) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        handleSendMessage(initialMessage);
        // Clear the initialMessage after sending
        if (onInitialMessageSent) {
          onInitialMessageSent();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [initialMessage]);

  return (
    <div className="mw-chat-container">
      {/* Messages Area */}
      <div className="mw-chat-messages-container">
        <div className="mw-chat-messages">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`mw-chat-message ${msg.type === "user" ? "user" : "bot"
                } ${msg.isStreaming ? "streaming" : ""} ${msg.isError ? "error" : ""
                }`}
            >
              <div className="mw-chat-message-content">
                <div
                  className={`mw-chat-message-bubble ${msg.isLeadCapture ? "lead-capture-highlight" : ""
                    }`}
                >
                  {/* TOP AREA - Logo + Type */}
                  {msg.type !== "user" && (
                    <div className="mw-chat-bot-header">
                      <img
                        src={WealthLogo}
                        alt="Bot"
                        className="mw-chat-bot-avatar"
                      />
                      <span className="mw-chat-bot-name">OM Assistant</span>
                    </div>
                  )}

                  {/* MESSAGE TEXT */}
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ node, ...props }) => (
                        <p className="mw-chat-markdown-p" {...props} />
                      ),
                      ul: ({ node, ...props }) => (
                        <ul className="mw-chat-markdown-ul" {...props} />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol className="mw-chat-markdown-ol" {...props} />
                      ),
                      li: ({ node, ...props }) => (
                        <li className="mw-chat-markdown-li" {...props} />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          className="mw-chat-markdown-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          {...props}
                        />
                      ),
                      code: ({ node, inline, ...props }) =>
                        inline ? (
                          <code className="mw-chat-inline-code" {...props} />
                        ) : (
                          <code className="mw-chat-code-block" {...props} />
                        ),
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>

                  {/* FOLLOWUP BUTTONS */}
                  {msg.followup_options?.length > 0 && (
                    <div className="mw-chat-followup-bubble">
                      {msg.followup_options.map((opt) => (
                        <button
                          key={opt.id}
                          className="mw-chat-followup-chip"
                          onClick={() => handleSendMessage(opt.text)}
                        >
                          {opt.text}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* CURSOR */}
                  {msg.isStreaming && (
                    <span className="mw-chat-cursor">|</span>
                  )}
                </div>

                {/* TIMESTAMP */}
                <div className="mw-chat-message-time">{msg.timestamp}</div>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && !messages.find((m) => m.isStreaming) && (
            <div className="mw-chat-message bot">
              <div>
                <img
                  src={WealthLogo}
                  alt="Bot"
                  className="mw-chat-bot-avatar-small"
                />
              </div>
              <div className="mw-chat-message-content">
                <div className="mw-chat-message-bubble">
                  <div className="mw-chat-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="mw-chat-input-container">
        <div className="mw-chat-input-wrapper">
          <input
            type="text"
            className={`mw-chat-input ${leadCaptureMode ? "lead-capture-mode" : ""
              }`}
            placeholder={
              leadCaptureMode
                ? `Please fill the form above...`
                : "Ask Me Anything..."
            }
            value={input}
            onChange={(e) => {
              if (!leadCaptureMode) {
                setInput(e.target.value);
                resetIdleTimer();
                setIdleCount(0);
              }
            }}
            onKeyPress={(e) => {
              if (leadCaptureMode) {
                e.preventDefault();
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isLoading || leadCaptureMode}
          />

          <button
            className="mw-chat-send-btn"
            onClick={() => handleSendMessage()}
            disabled={isLoading || !input.trim() || leadCaptureMode}
          >
            <FaPaperPlane />
          </button>
        </div>
      </div>

      {showMeetingForm ? (
        <ScheduleMeeting
          visitorId={uid}
          prefilledData={zohoVisitorData}
          onClose={() => setShowMeetingForm(false)}
          updateLead={updateLead}
          onLeadFormSubmit={handleLeadFormSubmit}
        />
      ) : showLeadForm ? (
        <LeadCaptureBottomSheet
          open={showLeadForm}
          onClose={() => setShowLeadForm(false)}
          title={leadFormTitle}
          allowSkip={allowLeadFormSkip}
          onSubmit={(formData) => {
            console.log("Lead form:", formData);
            setShowLeadForm(false);
            handleLeadFormSubmit(formData);
          }}
        />
      ) : null}
    </div>
  );
};

export default ProspectChatBot;
